"use client"

import { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"

import type { StorefrontPopup } from "@lib/data/popup-modal"
import X from "@modules/common/icons/x"

const STORAGE_KEY = "storefront-popup-dismissed-v1"

type StoredState = {
  signature: string
}

function buildSignature(settings: StorefrontPopup): string {
  return [settings.updatedAt ?? "", settings.imageUrl ?? "", settings.enabled ? "1" : "0"].join("|")
}

export default function StorefrontPopupModal({ settings }: { settings: StorefrontPopup }) {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)

  const signature = useMemo(() => buildSignature(settings), [settings])
  const hasImage = Boolean(settings.enabled && settings.imageUrl)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return
    if (!hasImage) {
      setVisible(false)
      return
    }

    const storedRaw = localStorage.getItem(STORAGE_KEY)
    let stored: StoredState | null = null
    if (storedRaw) {
      try {
        stored = JSON.parse(storedRaw) as StoredState
      } catch {
        stored = null
      }
    }

    if (!stored || stored.signature !== signature) {
      setVisible(true)
    }
  }, [mounted, hasImage, signature])

  const handleClose = () => {
    if (!hasImage) return
    setVisible(false)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ signature }))
  }

  if (!mounted || !visible || !hasImage || !settings.imageUrl) {
    return null
  }

  const content = (
    <div className="relative w-[min(90vw,560px)] max-w-3xl">
      <button
        type="button"
        onClick={handleClose}
        aria-label="Cerrar popup"
        className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/70"
      >
        <X size={18} />
      </button>
      {settings.linkUrl ? (
        <Link href={settings.linkUrl} onClick={handleClose} className="block" aria-label="Abrir enlace del popup">
          <Image
            src={settings.imageUrl}
            alt="Popup promocional"
            width={1120}
            height={1400}
            className="h-full w-full object-contain shadow-2xl"
            sizes="(max-width: 768px) 90vw, 560px"
          />
        </Link>
      ) : (
        <Image
          src={settings.imageUrl}
          alt="Popup promocional"
          width={1120}
          height={1400}
          className="h-full w-full object-contain shadow-2xl"
          sizes="(max-width: 768px) 90vw, 560px"
        />
      )}
    </div>
  )

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0" onClick={handleClose} aria-hidden />
      <div className="relative z-10 flex items-center justify-center" onClick={(event) => event.stopPropagation()}>
        {content}
      </div>
    </div>
  )
}
